/**
 * rewriteBlockExecutor — `rewrite_block` : réécrire EN PLACE le texte d'une
 * liste de paragraphes, en un appel.
 *
 * WHY une liste : réécrire un modèle Word d'avocat pour un autre client coûtait
 * un appel par paragraphe (34 appels, 13 min 25 sur une lettre de 57
 * paragraphes, dont 19 s d'éditeur — mesure du 2026-10-09 dans la vraie
 * application). Le texte de N paragraphes part ici par UN plan de l'éditeur.
 *
 * ## Ce que l'éditeur fait, mesuré (§17 de `superdoc-document-api.md`)
 *
 * - Une étape `text.rewrite` adressée par BLOC remplace tout le CONTENU d'un
 *   paragraphe par du texte : sa forme de paragraphe et son identifiant sont
 *   gardés, une image en fin de paragraphe aussi. Un plan de 150 étapes
 *   s'applique en 0,85 s et laisse UNE entrée d'historique.
 * - ⚠️ Ce que le paragraphe portait d'autre que du texte part avec l'ancien
 *   contenu : l'appel de note AVEC sa note, le saut de ligne, la tabulation, la
 *   révision en attente d'un confrère ; champ, lien et signet restent, vidés,
 *   en fin de paragraphe (§17.6). L'éditeur rend `success`.
 * - Un paragraphe SANS texte s'écrit de même. Il reçoit la forme de caractère
 *   par défaut ; seules les marques du texte y sont posées.
 * - Sous suivi activé, le plan suivi laisse une révision par paragraphe ; les
 *   rejeter rend le texte et la forme d'origine de chaque passage (§17.5).
 * - ⚠️ Tout le texte écrit prend la forme de caractère du PREMIER passage du
 *   paragraphe. D'où le second plan, de forme : il pose les marques `**`, `*`,
 *   `__` que le texte portait (`marquesDeTexte.ts`), et rend au texte non marqué
 *   la forme qui couvrait le plus de texte (`formeDuTexteReecrit`). Ce qu'une
 *   PARTIE du texte d'avant portait d'autre — les sept réglages et la forme hors
 *   de ces réglages — est nommé au reçu avec le texte qui le portait : à reposer
 *   quand le texte neuf porte encore ce texte (`rewrite.formattingNotCarried`,
 *   reçu `partial`), pour information quand il ne le porte plus
 *   (`rewrite.formattingDropped`).
 *
 * ## Ne rien détruire en silence
 *
 * Trois sortes de paragraphes ne sont PAS soumis au plan de texte
 * (`reecritureDeParagraphes.ts`) :
 * - celui qui porte ce qu'une réécriture entière détruirait
 *   (`empechementDeReecriture`, sur ce que le lecteur de forme relève) : refusé
 *   avant toute écriture, nommé au reçu avec son issue — les verbes par passage,
 *   qui gardent ces éléments. Seule exception, sous suivi, le paragraphe dont
 *   l'unique révision en attente est la `replacement` d'une réécriture
 *   précédente : il est réécrit dans cette révision (`remplacementEntierEnAttente`) ;
 * - celui dont le texte est déjà celui demandé (`texteDejaEnPlace`) : la forme
 *   calculée après une réécriture suppose que tout le texte a pris la forme du
 *   premier passage, ce qui est faux quand rien n'est réécrit — elle écraserait
 *   une étiquette que l'éditeur n'a pas touchée. Sans marque nouvelle il est
 *   compté à part (`rewrite.unchanged`) ; avec, seule cette marque est posée,
 *   sur son passage (`formeDuTexteEnPlace`) ;
 * - celui que l'éditeur refuse : sauté par le repli du socle.
 *
 * ## Les champs Word vides partent d'abord
 *
 * Un champ sans instruction ni résultat (reste de champ de fusion) est invisible
 * dans Word, mais le lecteur de forme le relève comme un champ : son paragraphe
 * serait refusé comme porteur d'une date ou d'un renvoi, et l'agent renverrait
 * l'avocat le compléter à la main (signalement du 2026-10-08). Ces champs sont
 * retirés des paragraphes à réécrire (`champsVides.ts`, qui porte les mesures et
 * la règle : écriture directe, champs VIDES seulement), APRÈS que la liste est
 * reconnue valide — une erreur dans l'appel ne retire rien. Chaque retrait fait
 * avancer la révision : l'écriture est alors rouverte sur le fichier d'après.
 * Un champ qui porte du contenu reste, et son paragraphe reste refusé.
 *
 * ## Le déroulé
 *
 * Celui du socle (`ecritureDeForme.ts`, « Un plan de texte avant la forme ») :
 * ouverture — fichier exporté et lu, clôturé sur la révision —, aperçu, plan de
 * texte dans le régime du document, plan de forme prouvé par la preuve commune
 * (`prouverLePlan`), clôture — le fichier relu est le seul juge. Deux exports
 * par appel, jamais un par paragraphe.
 *
 * Rien n'est muet : un paragraphe non réécrit est nommé au reçu (`partial`,
 * `rewrite.skipped`) et les autres sont écrits. Une erreur dans l'appel
 * lui-même (identifiant inconnu, d'un autre lieu, hors de la lecture) refuse
 * toute la liste avant d'écrire.
 *
 * ⚠️ Contrairement aux verbes de forme, ce verbe change du TEXTE : il garde la
 * portée d'inspection (`inspectedPublicNodeIds`).
 */

import type { CanvasActionInput } from '@hector/shared-types';

import type { CanvasActionReceipt } from './actionReceipt';
import { readCompleteTrackedChangesAtDocumentRevision, type CanvasSnapshotRevision } from './canvasSnapshot';
import { retirerLesChampsVidesDuBloc } from './champsVides';
import { ouvrirLEcritureDeForme, type EcritureDeFormeOuverte } from './ecritureDeForme';
import { REVISION_ABSENTE, revisionCourante } from './fencesDeMutation';
import { etapeDuPlan, prouverLePlan } from './planDeFormatText';
import {
  empechementDeReecriture,
  etapesDeFormeAEcrire,
  etapesDeTexte,
  formeDuTexteEnPlace,
  formeDuTexteReecrit,
  recuDeReecriture,
  remplacementEntierEnAttente,
  texteDejaEnPlace,
  viserLesParagraphes,
  type FormeDuTexte,
  type ParagrapheAReecrire,
} from './reecritureDeParagraphes';
import { modeDeRevision, type CanvasActionContext } from './textActionExecutors';

type Reecriture = Extract<CanvasActionInput, { action: 'rewrite_block' }>;

/**
 * Pose la forme de caractère du texte qui vient d'être écrit : un plan
 * `format.apply` direct, après le plan de texte — l'éditeur refuse une étape de
 * texte et une étape de forme sur le même bloc dans un plan (`TARGET_CONFLICT`,
 * §17.1). Les positions sont celles du texte ÉCRIT, et une étape par position
 * n'est écrite que sous la preuve commune du socle (`prouverLePlan`,
 * `planDeFormatText.ts`) : l'aperçu de l'éditeur la situe, d'UNE plage, dans ce
 * paragraphe, à cette position, sur ce texte.
 *
 * Rien n'est rendu : la forme posée se juge au fichier relu, comme le texte. Une
 * étape que l'éditeur refuse, ou qu'il situe ailleurs, laisse sa plage telle
 * quelle, et le reçu nomme le paragraphe (`rewrite.textFormatting.differentIn`).
 */
async function poserLaFormeDuTexte(ouverte: EcritureDeFormeOuverte, ecrits: readonly ParagrapheAReecrire[], formes: ReadonlyMap<string, FormeDuTexte>): Promise<void> {
  const aEcrire = ecrits.flatMap((paragraphe) => {
    const forme = formes.get(paragraphe.nodeId);
    return forme === undefined ? [] : etapesDeFormeAEcrire(paragraphe, forme);
  });
  if (aEcrire.length === 0) return;
  const prouve = await prouverLePlan(aEcrire, undefined, (steps) => ouverte.apercevoir(steps, 'text formatting'));
  // Un aperçu refusé en bloc, ou des positions que l'éditeur ne situe toujours pas, ne posent rien : le texte est
  // écrit, et la relecture dira la forme qui manque.
  if ('refus' in prouve || 'nonProuvees' in prouve) return;
  await ouverte.appliquerLesPlans(prouve.lots, 'forme');
}

/** L'écriture ouverte et les paragraphes visés sur le fichier lu, ou le reçu du refus. */
type ListeVisee = { ouverte: EcritureDeFormeOuverte; paragraphes: ParagrapheAReecrire[] } | { refus: CanvasActionReceipt };

async function ouvrirEtViser(ctx: CanvasActionContext, input: Reecriture): Promise<ListeVisee> {
  const ouverte = await ouvrirLEcritureDeForme(ctx, 'rewrite_block');
  if ('refus' in ouverte) return ouverte;
  const vises = viserLesParagraphes(ouverte.acte, input.blocks, ctx.inspectedPublicNodeIds, ouverte.lieuHorsDuCorpsDe);
  return 'refus' in vises ? { refus: ouverte.refuser(vises.refus) } : { ouverte, paragraphes: vises.paragraphes };
}

/**
 * Ouvre l'écriture sur une liste reconnue valide, les champs Word VIDES des
 * paragraphes à réécrire retirés (en-tête, « Les champs Word vides partent d'abord »).
 *
 * @returns `champsRetires` : le nombre de champs retirés, chacun une entrée d'historique AVANT celles de l'écriture.
 */
async function ouvrirSansChampsVides(ctx: CanvasActionContext, input: Reecriture): Promise<(Exclude<ListeVisee, { refus: unknown }> & { champsRetires: number }) | { refus: CanvasActionReceipt }> {
  const visee = await ouvrirEtViser(ctx, input);
  if ('refus' in visee) return visee;
  let champsRetires = 0;
  for (const paragraphe of visee.paragraphes) {
    if (texteDejaEnPlace(paragraphe) || !paragraphe.avant.horsTexte.includes('field')) continue;
    // L'ouverture a établi que la révision du document est celle que l'agent a lue ; chaque retrait la fait avancer.
    champsRetires += await retirerLesChampsVidesDuBloc(ctx, paragraphe.nodeId, await revisionCourante(ctx, REVISION_ABSENTE));
  }
  if (champsRetires === 0) return { ...visee, champsRetires };
  // Le fichier lu à l'ouverture porte encore les champs, et sa révision n'est plus celle du document : tout se relit.
  const rouverte = await ouvrirEtViser({ ...ctx, expectedRevision: await revisionCourante(ctx, REVISION_ABSENTE) }, input);
  return 'refus' in rouverte ? rouverte : { ...rouverte, champsRetires };
}

/** Dit au reçu les champs vides retirés avant l'écriture : des entrées d'historique que son compte ne porte pas. */
function avecLesChampsRetires(recu: CanvasActionReceipt, champsRetires: number): CanvasActionReceipt {
  if (champsRetires === 0) return recu;
  const dit = ` Before that, ${champsRetires} empty Word field(s) were removed from the paragraphs to rewrite (no instruction, no result: nothing the lawyer could see). `
    + `Each removal is one more history entry BEFORE this call's writes, not counted above.`;
  const [effet, ...autres] = recu.effects;
  if (effet !== undefined) return { ...recu, effects: [{ ...effet, description: effet.description + dit }, ...autres] };
  const [erreur, ...suivantes] = recu.errors;
  return erreur === undefined ? recu : { ...recu, errors: [{ ...erreur, message: erreur.message + dit }, ...suivantes] };
}

/**
 * Par paragraphe, la révision en attente que sa réécriture reprend sans la détruire (`remplacementEntierEnAttente`,
 * qui porte la mesure et la limite), telle que lue avant d'écrire.
 *
 * Sous suivi seulement : la mesure n'a été faite que là, et en direct l'écriture ne vit dans aucune révision — le
 * refus d'avant tient. Les révisions ne sont lues que si un paragraphe à réécrire en porte une au fichier, et en
 * entier, sous une révision de document stable : une liste incomplète ou lue pendant une frappe ne reprend rien.
 */
async function revisionsReprisesPar(ctx: CanvasActionContext, paragraphes: readonly ParagrapheAReecrire[]): Promise<Map<string, CanvasSnapshotRevision>> {
  if (ctx.regimeSuivi === false) return new Map();
  const porteurs = paragraphes.filter((paragraphe) => !texteDejaEnPlace(paragraphe) && paragraphe.avant.horsTexte.includes('pending_tracked_change'));
  if (porteurs.length === 0) return new Map();
  const lues = await lireToutesLesRevisions(ctx);
  if (lues === undefined) return new Map();
  return new Map(porteurs.flatMap((paragraphe) => {
    const revision = remplacementEntierEnAttente(paragraphe, lues);
    return revision === undefined ? [] : [[paragraphe.nodeId, revision] as const];
  }));
}

/** La liste complète des révisions ouvertes, à la révision courante du document ; `undefined` : elle n'est pas prouvée complète. */
async function lireToutesLesRevisions(ctx: CanvasActionContext): Promise<CanvasSnapshotRevision[] | undefined> {
  const lues = await readCompleteTrackedChangesAtDocumentRevision(ctx.doc, await revisionCourante(ctx, REVISION_ABSENTE));
  return lues.ok ? lues.trackedChanges : undefined;
}

export async function executeRewriteBlock(ctx: CanvasActionContext, input: Reecriture): Promise<CanvasActionReceipt> {
  const visee = await ouvrirSansChampsVides(ctx, input);
  if ('refus' in visee) return visee.refus;
  const { ouverte, paragraphes, champsRetires } = visee;
  const revisionsReprises = await revisionsReprisesPar(ctx, paragraphes);
  // Un texte déjà en place n'est PAS réécrit : l'éditeur donnerait… rien (étape sans effet), et le plan de forme, bâti
  // sur « tout le texte a pris la forme du premier passage », écraserait une étiquette qu'il n'a pas touchée.
  // Un paragraphe qui porte ce qu'une réécriture entière détruirait n'est pas soumis à l'éditeur : nommé au reçu, avec son issue.
  const sautes: Array<{ nodeId: string; raison: string }> = paragraphes.flatMap((paragraphe) => {
    const raison = empechementDeReecriture(paragraphe, revisionsReprises.has(paragraphe.nodeId));
    return raison === undefined ? [] : [{ nodeId: paragraphe.nodeId, raison }];
  });
  const empeches = new Set(sautes.map(({ nodeId }) => nodeId));
  const aReecrire = paragraphes.filter((paragraphe) => !texteDejaEnPlace(paragraphe) && !empeches.has(paragraphe.nodeId));
  const appliques = new Set<string>();
  const inchanges = new Set<string>();
  const formes = new Map<string, FormeDuTexte>();
  let texteSoumisEnEntier = true;

  if (aReecrire.length > 0) {
    const apercu = await ouverte.apercevoir(etapesDeTexte(aReecrire), 'text');
    if ('refus' in apercu) return avecLesChampsRetires(apercu.refus, champsRetires);
    for (const { stepId, refus } of apercu.refusees) {
      const paragraphe = etapeDuPlan(aReecrire, stepId);
      if (paragraphe !== undefined) sautes.push({ nodeId: paragraphe.nodeId, raison: refus.detail });
    }
    const [sort] = await ouverte.appliquerLesPlans([apercu.gardees], 'texte', { changeMode: modeDeRevision(ctx) });
    for (const stepId of sort?.ecrites ?? []) {
      const paragraphe = etapeDuPlan(aReecrire, stepId);
      if (paragraphe === undefined) continue;
      appliques.add(paragraphe.nodeId);
      formes.set(paragraphe.nodeId, formeDuTexteReecrit(paragraphe));
    }
    // L'éditeur dit le texte déjà en place alors que le fichier lu disait autre chose : rien n'est écrit, donc aucune forme non plus.
    for (const stepId of sort?.sansObjet ?? []) {
      const paragraphe = etapeDuPlan(aReecrire, stepId);
      if (paragraphe !== undefined) inchanges.add(paragraphe.nodeId);
    }
    for (const { stepId, refus } of sort?.sautees ?? []) {
      const paragraphe = etapeDuPlan(aReecrire, stepId);
      if (paragraphe !== undefined) sautes.push({ nodeId: paragraphe.nodeId, raison: refus.detail });
    }
    texteSoumisEnEntier = appliques.size + inchanges.size + (sautes.length - empeches.size) === aReecrire.length;
  }
  for (const paragraphe of paragraphes.filter(texteDejaEnPlace)) {
    const forme = formeDuTexteEnPlace(paragraphe);
    // Sans marque nouvelle, le paragraphe est inchangé ; avec, il n'est écrit que si la forme peut encore partir.
    if (forme.ecritures.length === 0) inchanges.add(paragraphe.nodeId);
    else if (texteSoumisEnEntier) {
      appliques.add(paragraphe.nodeId);
      formes.set(paragraphe.nodeId, forme);
    }
  }

  // La forme ne part que si chaque étape de texte a été soumise : après une interruption, plus rien ne s'écrit.
  if (texteSoumisEnEntier) await poserLaFormeDuTexte(ouverte, paragraphes.filter(({ nodeId }) => appliques.has(nodeId)), formes);

  const cloture = await ouverte.clore();
  // Sous suivi, la révision de CHAQUE paragraphe réécrit se juge sur la liste relue après l'écriture (`recuDeReecriture`) :
  // la reprise prouvée (`repriseProuvee`), ou une révision neuve ancrée sur lui. Sans cette relecture, aucune reprise
  // n'est dite suivie.
  const revisionsApres = ctx.regimeSuivi !== false && aReecrire.some(({ nodeId }) => appliques.has(nodeId)) ? await lireToutesLesRevisions(ctx) : undefined;
  return avecLesChampsRetires(
    recuDeReecriture({ demandes: paragraphes, appliques, inchanges, sautes, texteSoumisEnEntier, formes, regimeSuivi: ctx.regimeSuivi !== false, revisionsReprises, revisionsApres, cloture }),
    champsRetires,
  );
}
